import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, CalendarDays, Pill, Stethoscope, AlertCircle } from 'lucide-react';
import CalendarEventCard from '../components/calendar/CalendarEventCard';
import AppLogo from '../components/AppLogo';

const eventTypes = [
  { value: 'appointment', label: 'Appointment', icon: <Stethoscope className="w-4 h-4" />, color: 'bg-blue-500' },
  { value: 'medication', label: 'Medication', icon: <Pill className="w-4 h-4" />, color: 'bg-emerald-500' },
  { value: 'task', label: 'Task', icon: <CalendarDays className="w-4 h-4" />, color: 'bg-purple-500' },
  { value: 'reminder', label: 'Reminder', icon: <AlertCircle className="w-4 h-4" />, color: 'bg-amber-500' },
  { value: 'other', label: 'Other', icon: <CalendarDays className="w-4 h-4" />, color: 'bg-gray-500' }
];

const CalendarPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: '', description: '', event_type: 'appointment',
    start_time: '', end_time: '', location: '',
    attendees: [], all_day: false, recurrence: null
  });

  useEffect(() => { fetchData(); }, [circleId, selectedDate]);

  const fetchData = async () => {
    try {
      const [eventsRes, membersRes] = await Promise.all([
        axios.get(`${API}/circles/${circleId}/calendar-events`),
        axios.get(`${API}/circles/${circleId}/members`)
      ]);
      setEvents(eventsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      toast.error('Failed to fetch calendar data');
    } finally { setLoading(false); }
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/calendar-events`, { ...newEvent, circle_id: circleId });
      toast.success('Event added to calendar!');
      setAddEventDialogOpen(false);
      setNewEvent({ title: '', description: '', event_type: 'appointment', start_time: '', end_time: '', location: '', attendees: [], all_day: false, recurrence: null });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add event'); }
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        title: editEvent.title, description: editEvent.description,
        start_time: editEvent.start_time, end_time: editEvent.end_time,
        location: editEvent.location, attendees: editEvent.attendees, all_day: editEvent.all_day
      };
      await axios.put(`${API}/calendar-events/${editEvent.id}`, updateData, { params: updateData });
      toast.success('Event updated!');
      setEditDialogOpen(false);
      setEditEvent(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update event'); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/calendar-events/${eventId}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) { toast.error('Failed to delete event'); }
  };

  const getEventTypeInfo = (type) => eventTypes.find(t => t.value === type) || eventTypes[4];

  const getEventsForDate = (date) => events.filter(event => {
    const d = new Date(event.start_time);
    return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
  });

  const selectedDayEvents = getEventsForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="calendar-page">
      <div className="surface-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppLogo />
              <div>
                <h1 className="text-2xl font-bold text-foreground">Shared Calendar</h1>
                <p className="text-sm text-muted-foreground">Coordinate appointments, medications, and important dates</p>
              </div>
            </div>

            <Dialog open={addEventDialogOpen} onOpenChange={setAddEventDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-event-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="surface-card max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add Calendar Event</DialogTitle>
                  <DialogDescription>Schedule an appointment, medication, or important date</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddEvent} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="event-type">Event Type</Label>
                      <Select value={newEvent.event_type} onValueChange={(value) => setNewEvent({ ...newEvent, event_type: value })}>
                        <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {eventTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">{type.icon} {type.label}</div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="event-title">Title *</Label>
                      <Input id="event-title" data-testid="event-title-input" placeholder="e.g., Doctor Appointment" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea id="description" data-testid="description-input" placeholder="Additional details..." value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} className="resize-none" rows={2} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start-time">Start Date & Time *</Label>
                      <Input id="start-time" type="datetime-local" data-testid="start-time-input" value={newEvent.start_time} onChange={(e) => setNewEvent({ ...newEvent, start_time: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end-time">End Date & Time</Label>
                      <Input id="end-time" type="datetime-local" data-testid="end-time-input" value={newEvent.end_time} onChange={(e) => setNewEvent({ ...newEvent, end_time: e.target.value })} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch checked={newEvent.all_day} onCheckedChange={(checked) => setNewEvent({ ...newEvent, all_day: checked })} data-testid="all-day-switch" />
                    <Label>All Day Event</Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input id="location" data-testid="location-input" placeholder="Address or virtual meeting link" value={newEvent.location} onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })} />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="attendees">Attendees</Label>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        const current = newEvent.attendees || [];
                        if (!current.includes(value)) setNewEvent({ ...newEvent, attendees: [...current, value] });
                      }}
                    >
                      <SelectTrigger data-testid="attendees-select"><SelectValue placeholder="Select circle members" /></SelectTrigger>
                      <SelectContent>
                        {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.user?.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {newEvent.attendees && newEvent.attendees.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {newEvent.attendees.map((id) => {
                          const member = members.find(m => m.user_id === id);
                          return member ? (
                            <span key={id} className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs flex items-center gap-1">
                              {member.user?.name}
                              <button type="button" onClick={() => setNewEvent({ ...newEvent, attendees: newEvent.attendees.filter(a => a !== id) })} className="hover:text-primary/80">&times;</button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>

                  <Button type="submit" className="w-full btn-primary" data-testid="submit-event-btn">Add to Calendar</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Card className="surface-card">
              <CardContent className="pt-6">
                <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} className="rounded-md border" />
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold text-foreground">
              Events for {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </h2>

            {selectedDayEvents.length > 0 ? (
              <div className="space-y-3">
                {selectedDayEvents.map((event) => (
                  <CalendarEventCard
                    key={event.id}
                    event={event}
                    eventTypeInfo={getEventTypeInfo(event.event_type)}
                    onEdit={(evt) => { setEditEvent(evt); setEditDialogOpen(true); }}
                    onDelete={handleDeleteEvent}
                  />
                ))}
              </div>
            ) : (
              <Card className="surface-card">
                <CardContent className="py-12 text-center">
                  <CalendarDays className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">No events scheduled for this day</p>
                  <Button size="sm" onClick={() => setAddEventDialogOpen(true)} className="btn-primary mt-4">Add Event</Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {editEvent && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="surface-card max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
              <DialogDescription>Update event details</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditEvent} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input id="edit-title" value={editEvent.title} onChange={(e) => setEditEvent({ ...editEvent, title: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea id="edit-description" value={editEvent.description || ''} onChange={(e) => setEditEvent({ ...editEvent, description: e.target.value })} className="resize-none" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-start-time">Start Date & Time</Label>
                  <Input id="edit-start-time" type="datetime-local" value={editEvent.start_time?.slice(0, 16)} onChange={(e) => setEditEvent({ ...editEvent, start_time: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-end-time">End Date & Time</Label>
                  <Input id="edit-end-time" type="datetime-local" value={editEvent.end_time?.slice(0, 16) || ''} onChange={(e) => setEditEvent({ ...editEvent, end_time: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-location">Location</Label>
                <Input id="edit-location" value={editEvent.location || ''} onChange={(e) => setEditEvent({ ...editEvent, location: e.target.value })} />
              </div>
              <Button type="submit" className="w-full btn-primary">Save Changes</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CalendarPage;
